import {
  ChangeEvent,
  forwardRef,
  ReactNode,
  RefForwardingComponent,
  useEffect,
} from 'react'
import {
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Select,
} from '@material-ui/core'
import { FormLabelProps } from '@material-ui/core/FormLabel'
import { InputProps as MuiInputProps } from '@material-ui/core/Input'
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useDebouncedCallback } from 'use-debounce'
import CheckIcon from 'svgIcons/Check'
import ExpandMoreIcon from 'svgIcons/ExpandMore'
import { getInputHelperID, getInputLabelID } from 'utils/domElementID'
import LabelWithHelper, { LabelRequiredVariant } from './LabelWithHelper'

export const useStyle = makeStyles(
  (theme) => ({
    root: {},
    select: {
      minWidth: 250,
    },
    textFieldEndAdornmentGridContainer: {
      'div + &': {
        marginTop: 10,
      },
    },
    textFieldStartAdornmentGridItem: {
      marginRight: 5,
      display: 'flex',
      whiteSpace: 'nowrap',
    },
    textFieldEndAdornmentGridItem: {
      marginLeft: 5,
      display: 'flex',
      whiteSpace: 'nowrap',
    },
    checkIcon: {
      fontSize: 14,
      color: theme.palette.primary.main,
    },
    expandMore: {
      fontSize: 14,
    },
    hintMessage: {
      fontSize: 12,
      fontWeight: 400,
      marginTop: 10,
      color: theme.palette.text.hint,
    },
  }),
  { name: 'CustomTextField' },
)

type TextFieldProps = Omit<MuiTextFieldProps, 'variant' | 'classes'> & {
  textFieldEndAdornment?: ReactNode
  textFieldStartAdornment?: ReactNode
  onDebounceChange?(value: string): void
  debounceDelayMS?: number
  isAsyncValid?: boolean
  isAsyncValidating?: boolean
  labelRequiredVariant?: LabelRequiredVariant
  FormLabelProps?: FormLabelProps
  hintMessage?: string
  showEmptyHintMessage?: boolean
}

const TextField: RefForwardingComponent<HTMLDivElement, TextFieldProps> = (
  props,
  ref,
) => {
  const {
    autoComplete,
    autoFocus = false,
    children,
    className,
    color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    FormHelperTextProps,
    fullWidth = false,
    helperText,
    hiddenLabel,
    hintMessage,
    id,
    FormLabelProps,
    inputProps,
    InputProps,
    inputRef,
    label,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required = false,
    rows,
    rowsMax,
    select = false,
    SelectProps,
    type,
    value,
    textFieldEndAdornment,
    textFieldStartAdornment,
    debounceDelayMS = 0,
    onDebounceChange,
    isAsyncValid = false,
    isAsyncValidating = false,
    labelRequiredVariant = 'default',
    ...other
  } = props
  const classes = useStyle()

  const [debouncedCallback, cancel] = useDebouncedCallback((value) => {
    if (onDebounceChange) {
      onDebounceChange(value)
    }
  }, debounceDelayMS)

  useEffect(() => {
    if (!debounceDelayMS) {
      cancel()
    }
  }, [debounceDelayMS, cancel])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event)
    }

    debouncedCallback(event.target.value)
  }

  const InputMore: Partial<MuiInputProps> = {}

  if (select) {
    // unset defaults from textbox inputs
    if (!SelectProps || !SelectProps.native) {
      InputMore.id = undefined
    }
    InputMore['aria-describedby'] = undefined
  }

  const helperTextId = helperText && id ? getInputHelperID(id) : undefined
  const inputLabelId = label && id ? getInputLabelID(id) : undefined

  const asyncValidating = (
    <InputAdornment position="end">
      <IconButton>
        <CircularProgress size={20} data-testid="TextField-CircularProgress" />
      </IconButton>
    </InputAdornment>
  )
  const asyncValidatingPass = (
    <InputAdornment position="end">
      <IconButton>
        <CheckIcon
          className={classes.checkIcon}
          data-testid="TextField-CheckIcon"
        />
      </IconButton>
    </InputAdornment>
  )

  let endAdornment
  if (isAsyncValidating) {
    endAdornment = asyncValidating
  } else if (isAsyncValid && !error) {
    endAdornment = asyncValidatingPass
  } else {
    endAdornment = InputProps?.endAdornment
  }

  const InputElement = (
    <Input
      disableUnderline
      error={error}
      aria-describedby={helperTextId}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      defaultValue={defaultValue}
      fullWidth={fullWidth}
      multiline={multiline}
      name={name}
      rows={rows}
      rowsMax={rowsMax}
      type={type}
      value={value}
      id={id}
      inputRef={inputRef}
      onBlur={onBlur}
      onChange={handleChange}
      onFocus={onFocus}
      placeholder={placeholder}
      inputProps={inputProps}
      {...InputMore}
      {...InputProps}
      endAdornment={endAdornment}
    />
  )

  const input = select ? (
    <Select
      aria-describedby={helperTextId}
      id={id}
      labelId={inputLabelId}
      value={value}
      input={InputElement}
      IconComponent={ExpandMoreIcon}
      displayEmpty
      {...SelectProps}
    >
      {children}
    </Select>
  ) : (
    InputElement
  )

  return (
    <FormControl
      className={clsx(classes.root, { [classes.select]: select }, className)}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      hiddenLabel={hiddenLabel}
      ref={ref}
      required={required}
      color={color}
      {...other}
    >
      <LabelWithHelper
        helperText={helperText}
        label={label}
        required={required}
        FormLabelProps={{
          htmlFor: id,
          id: inputLabelId,
          ...FormLabelProps,
        }}
        FormHelperTextProps={{
          id: helperTextId,
          ...FormHelperTextProps,
        }}
        labelRequiredVariant={labelRequiredVariant}
      />
      {textFieldEndAdornment || textFieldStartAdornment ? (
        <Grid
          wrap="nowrap"
          container
          alignItems="stretch"
          className={classes.textFieldEndAdornmentGridContainer}
        >
          {textFieldStartAdornment && (
            <Grid item className={classes.textFieldStartAdornmentGridItem}>
              {textFieldStartAdornment}
            </Grid>
          )}
          <Grid item xs>
            {input}
          </Grid>
          {textFieldEndAdornment && (
            <Grid item className={classes.textFieldEndAdornmentGridItem}>
              {textFieldEndAdornment}
            </Grid>
          )}
        </Grid>
      ) : (
        input
      )}
      {hintMessage && <h6 className={classes.hintMessage}>{hintMessage}</h6>}
    </FormControl>
  )
}

export default forwardRef(TextField)
